import type {
  GfxCommonBlockProps,
  GfxElementGeometry,
} from '@blocksuite/std/gfx';
import { GfxCompatible } from '@blocksuite/std/gfx';
import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

import type { TextAlign } from '../../consts';
import type { BlockMeta } from '../../utils/types.js';

export { isCatalogImage } from '@streamlain/database-identity';
import { ImageBlockTransformer } from './image-transformer.js';

export type ImageBlockProps = {
  caption?: string;
  /** File name of this concrete image occurrence. */
  name?: string;
  /** Canonical MIME type of the referenced image blob. */
  type?: string;
  sourceCaption?: string;
  presentation?: ImagePresentation;
  sourceId?: string;
  /**
   * The blob a catalogue insert (template, slide, sticker) placed; see
   * isCatalogImage. Empty for every other image.
   */
  catalogSourceId?: string;
  width?: number;
  height?: number;
  rotate: number;
  size?: number;
  comments?: Record<string, boolean>;
  textAlign?: TextAlign;
} & Omit<GfxCommonBlockProps, 'scale'> &
  BlockMeta;

export type ImagePresentation = 'default' | 'polaroid';

const defaultImageProps = {
  caption: '',
  name: '',
  type: '',
  sourceCaption: '',
  // Polaroid is an insertion choice, not a universal image default. User
  // uploads on the edgeless canvas opt into it in addImages(), while imported,
  // generated, and template images must remain plain unless explicitly styled.
  presentation: 'default',
  sourceId: '',
  catalogSourceId: '',
  width: 0,
  height: 0,
  index: 'a0',
  lockedBySelf: false,
  rotate: 0,
  size: -1,
  comments: undefined,
  textAlign: undefined,
  'meta:createdAt': undefined,
  'meta:createdBy': undefined,
  'meta:updatedAt': undefined,
  'meta:updatedBy': undefined,
} satisfies Omit<ImageBlockProps, 'xywh'>;

export const ImageBlockSchema = defineBlockSchema({
  flavour: 'affine:image',
  props: (): ImageBlockProps => defaultImageProps as ImageBlockProps,
  metadata: {
    version: 1,
    role: 'content',
  },
  transformer: transformerConfigs =>
    new ImageBlockTransformer(transformerConfigs),
  toModel: () => new ImageBlockModel(),
});

export const ImageBlockSchemaExtension = BlockSchemaExtension(ImageBlockSchema);

export class ImageBlockModel
  extends GfxCompatible<ImageBlockProps>(BlockModel)
  implements GfxElementGeometry {}
